import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { checkBibExists } from '@/core/services/claim';

interface BibCheckStepProps {
  raceId: string;
  onSuccess: (bib: string) => void;
  onNeedInquiry: () => void;
}

export function BibCheckStep({ raceId, onSuccess, onNeedInquiry }: BibCheckStepProps) {
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  const [bib, setBib] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bib.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const result = await checkBibExists(raceId, bib.trim());

      if (!result.exists) {
        setError('完走データがありません');
        return;
      }

      if (!result.hasVerificationData) {
        onNeedInquiry();
        return;
      }

      if (!result.hasFinishData) {
        setError('完走データがありません');
        return;
      }

      onSuccess(bib.trim());
    } catch (err) {
      setError('エラーが発生しました。もう一度お試しください');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <LocalOfferIcon
          sx={{
            color: theme.palette.primary.main,
            fontSize: 28,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            background: gradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          本人確認
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        デジタル完走証を受け取るには、エントリー時の情報で本人確認を行います。
        <br />
        まず、ゼッケン番号を入力してください。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="ゼッケン番号"
        value={bib}
        onChange={(e) => setBib(e.target.value)}
        placeholder="例: 1234"
        disabled={loading}
        inputProps={{ inputMode: 'numeric' }}
        sx={{
          mb: 4,
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
            },
          },
        }}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading || !bib.trim()}
        size="large"
        sx={{
          background: gradient,
          color: '#fff',
          height: 48,
          fontWeight: 600,
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            background: gradient,
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
          },
          '&:disabled': {
            background: theme.palette.action.disabledBackground,
          },
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : '確認'}
      </Button>
    </Box>
  );
}
